import React from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../button";
import { TextInput } from "../../form";
import { useLoading } from "../../hooks";
import { Icon } from "../../icons";
import { useTranslation } from "../../localization";
import { ConfirmModal, useAsyncModal } from "../../modal";
import { useFileExplorerContext } from "./context";
import { findLogestPath, selectionPasses } from "./shared";

export function FileExplorerCreateFolder() {
	/** Context */
	const {
		handleOnFolderDrop,
		handleOnAfterFileAction,
		selectedFiles,
		structure,
		filesById,
		canUpdateFile,
	} = useFileExplorerContext();

	/** Hooks */
	const methods = useForm<{
		folderName: string;
	}>();
	const createFolderLoader = useLoading();
	const { t } = useTranslation();
	const asyncModal = useAsyncModal<string>();

	/** Computed */
	// A folder here is a path, so putting the selection into one rewrites every selected file's
	// path, which each file's `canUpdateFile` has to allow.
	const mayMoveSelection = selectionPasses(selectedFiles, filesById, canUpdateFile);

	/** Functions */
	async function handleOnCreateFolder() {
		try {
			createFolderLoader.start();
			const folderName = await asyncModal.confirm();
			if (typeof folderName !== "string") return;
			const longestPath = findLogestPath(structure, selectedFiles) || "";
			const name = [longestPath, folderName].filter(Boolean).join("/");
			await handleOnFolderDrop(name, selectedFiles);
			await handleOnAfterFileAction();
		} catch (error) {
			console.error("Error creating folder:", error);
		} finally {
			createFolderLoader.stop();
		}
	}

	async function handleOnResolve() {
		const folderName = methods.getValues("folderName");
		if (!folderName) return false;
		methods.reset();
		return folderName;
	}

	/** Render */
	return (
		<React.Fragment>
			<Button
				icon={<Icon name="FolderPlus" />}
				onClick={handleOnCreateFolder}
				isLoading={createFolderLoader.isLoading}
				isDisabled={!mayMoveSelection}
			>
				{t("files.create_folder")}
			</Button>
			<ConfirmModal
				controller={asyncModal}
				message={null}
				title={t("files.create_folder")}
				onResolve={handleOnResolve}
				confirmVariant="primary"
				confirmLabel={t("files.create_folder")}
			>
				<TextInput
					control={methods.control}
					name="folderName"
					label={t("files.folder_name")}
					placeholder="Some Folder..."
					isRequired
				/>
			</ConfirmModal>
		</React.Fragment>
	);
}
